import Inherit from '../../core/inherit';
import ContractsDelegate from './contracts.delegate';
import ShelfController from '../../core/shelf.controller';
import { getStorePrice, getStorePriceMap, getStoreIds } from '../../utils/tradeInvestment';

const ALL = 'all';

const EMPTY_FILTERS = { search: '', channel: ALL, clientId: ALL, storeId: ALL, type: ALL };

class ContractsController extends Inherit(ShelfController, ContractsDelegate) {
    async initialize() {
        let contracts = null;
        let stores = null;
        let clients = null;
        if (this.isAuthenticated) {
            contracts = await this.data.fetchContracts();
            stores = await this.data.fetchAllStores();
            clients = await this.data.fetchClients();

            // Check if we're in edit mode
            const editId = this.props.searchParams?.get('edit');
            if (editId) {
                const contractToEdit = contracts.find((c) => c._id === editId);
                if (contractToEdit) {
                    this.setState({
                        contracts,
                        loading: false,
                        stores,
                        clients,
                        isOpen: true,
                        editMode: true,
                        editingContract: contractToEdit,
                        ...this.buildContractRows(contracts, stores, clients, this.state.filters || EMPTY_FILTERS)
                    });
                    return;
                }
            }
        } else this.navigateLogin();

        this.setState({
            contracts,
            loading: false,
            stores,
            clients,
            ...this.buildContractRows(contracts, stores, clients, EMPTY_FILTERS)
        });
    }

    /**
     * Aplica los filtros de búsqueda / canal / cliente / PDV / tipo y calcula la
     * inversión de cada contratación sumando únicamente los pisos que pasan el
     * filtro, de modo que el total siempre corresponde a lo que se está viendo.
     */
    buildContractRows(contracts = [], stores = [], clients = [], filters = EMPTY_FILTERS) {
        const clientById = new Map((clients || []).map((client) => [String(client._id), client]));
        const storeById = new Map((stores || []).map((store) => [String(store._id), store]));

        const storeMatchesFilters = (storeId) => {
            const store = storeById.get(String(storeId));
            if (filters.storeId !== ALL) return String(storeId) === String(filters.storeId);
            if (filters.clientId !== ALL && String(store?.client) !== String(filters.clientId)) return false;
            if (filters.channel !== ALL) {
                const client = clientById.get(String(store?.client));
                if (client?.channel !== filters.channel) return false;
            }
            return true;
        };

        const hasStoreFilters = filters.storeId !== ALL || filters.clientId !== ALL || filters.channel !== ALL;
        const search = (filters.search || '').trim().toLowerCase();

        const rows = [];
        let totalInvestment = 0;
        let totalStores = 0;

        (contracts || []).forEach((contract) => {
            if (filters.type !== ALL && contract.type !== filters.type) return;
            if (search && !(contract.name || '').toLowerCase().includes(search)) return;

            const visibleStoreIds = getStoreIds(contract).filter(storeMatchesFilters);
            if (hasStoreFilters && visibleStoreIds.length === 0) return;

            const priceMap = getStorePriceMap(contract);
            const investment = visibleStoreIds.reduce((total, storeId) => total + getStorePrice(contract, storeId, priceMap), 0);

            totalInvestment += investment;
            totalStores += visibleStoreIds.length;
            rows.push({ ...contract, investment, visibleStoreCount: visibleStoreIds.length });
        });

        return {
            rows,
            filters,
            investment: {
                total: totalInvestment,
                contractsCount: rows.length,
                storesCount: totalStores,
                isFiltered: hasStoreFilters || filters.type !== ALL || Boolean(search)
            }
        };
    }

    applyContractFilters(filters) {
        const { contracts, stores, clients } = this.state;
        this.setState(this.buildContractRows(contracts, stores, clients, filters));
    }

    async createContract(payload) {
        try {
            this.setState({ loading: true });
            const contract = await this.data.createContract(payload);

            if (!contract.name) this.setState({ loading: false });
            else {
                await this.reloadContracts();
                this.setIsOpen(false);
            }
        } catch (err) {
            console.log(err);
            this.setState({ loading: false });
        }
    }

    async updateContract(contractId, payload) {
        try {
            this.setState({ loading: true });
            const contract = await this.data.updateContract(contractId, payload);

            if (!contract._id) this.setState({ loading: false });
            else {
                await this.reloadContracts();
                this.setIsOpen(false);
            }
        } catch (err) {
            console.log(err);
            this.setState({ loading: false });
        }
    }

    async reloadContracts() {
        const contracts = await this.data.fetchContracts();
        const { stores, clients, filters } = this.state;
        this.setState({
            contracts,
            loading: false,
            ...this.buildContractRows(contracts, stores, clients, filters || EMPTY_FILTERS)
        });
    }

    openEditDrawer(contract) {
        this.setState({
            isOpen: true,
            editMode: true,
            editingContract: contract
        });
    }

    navigateContract(id) {
        if (this.props.navigate) {
            this.props.navigate(`/dashboard/trade/contracts/contract?id=${id}`);
        } else {
            console.error('Navigate function not available in props');
        }
    }
}

export default ContractsController;
